import { useState, useRef, useCallback, useEffect } from "react";
import type {
	ChatMessage,
	ChatMessageFromDb,
	ChatStreamChunk,
	ContextWarningPayload,
	ChatErrorPayload,
	ClassificationPayload,
	SourcesPayload,
	RetrievalEndPayload,
	ChartDataPayload,
	ChartSpec,
	SSEEvent,
	ActionProposal,
	ActionResult,
} from "../types/index.js";
import { apiGet, apiPost, apiPatch } from "../lib/api.js";
import { parseSSEStream } from "../lib/sse.js";

/**
 * Produce a human-friendly error message from a failed action response,
 * branching on the HTTP status code to give the user contextual guidance.
 */
function resolveActionErrorMessage(status: number, serverMessage: string | undefined): string {
	switch (status) {
		case 404:
			return "This action has expired. Please try again.";
		case 429:
			return "Too many actions in a short period. Please wait a moment before trying again.";
		case 403:
			return "Unauthorised — you may need to sign in again to perform this action.";
		default:
			return serverMessage ?? "An unexpected error occurred whilst executing the action.";
	}
}

/** Maximum number of messages to include in conversation history. */
const MAX_HISTORY_MESSAGES = 20;

/** Map "assistant" to Gemini's "model" role (used for DB storage and history alike). */
function toModelRole(role: "user" | "assistant"): "user" | "model" {
	return role === "assistant" ? "model" : role;
}

/** Map DB "model" role to frontend "assistant" role for display. */
function toDisplayRole(role: "user" | "model"): "user" | "assistant" {
	return role === "model" ? "assistant" : role;
}

/** Generate a unique message ID for in-memory display state. */
function generateId(): string {
	return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

interface UseChatCallbacks {
	/** Called when a session needs to be created; resolves to the new session ID. */
	onSessionNeeded: (projectId: string | null) => Promise<string>;
	/** Called after messages have been saved, to refresh the session list. */
	onMessagesSaved: () => void;
	/** Called when a new session has been assigned, to sync the active session ID upward. */
	onSessionIdChange: (id: string) => void;
}

interface UseChatReturn {
	messages: ChatMessage[];
	isStreaming: boolean;
	contextWarning: ContextWarningPayload | null;
	/** Send a message. Entity tokens ([@Name:type_ID]) are parsed automatically. */
	sendMessage: (text: string) => Promise<void>;
	clearContextWarning: () => void;
	/** Approve a proposed action — calls POST /api/actions/execute. */
	handleApproveAction: (actionId: string) => Promise<void>;
	/** Reject a proposed action — calls POST /api/actions/:actionId/reject. */
	handleRejectAction: (actionId: string) => Promise<void>;
	/** True immediately after a session's messages are loaded — use to trigger instant scroll-to-bottom. */
	justLoaded: boolean;
	/** Resets justLoaded to false after the scroll-to-bottom has been applied. */
	resetJustLoaded: () => void;
}

/**
 * Chat hook — manages conversation state, SSE streaming, and session persistence.
 *
 * Handles SSE event types from the backend:
 * - `message`           — streamed text chunks from Gemini
 * - `classification`    — model, complexity, and intent used for the response
 * - `sources`           — document sources retrieved via RAG (includes workdriveFileId, documentId)
 * - `chart_data`        — chart specification for visualisation responses
 * - `retrieval_start`   — document search has begun
 * - `retrieval_end`     — document search has completed (found/none/unavailable)
 * - `context_warning`   — project context could not be loaded
 * - `error`             — backend/Gemini error
 *
 * Session lifecycle:
 * - When activeSessionId changes to a non-null value, loads messages from the DB.
 * - When a message is sent with no active session, creates one via onSessionNeeded.
 * - After a stream completes, saves the user+assistant pair to the DB.
 * - Auto-titles the session with the first 80 chars of the first user message.
 * - Clears messages when selectedProjectId changes (active project switch).
 */
export function useChat(
	selectedProjectId: string | null,
	activeSessionId: string | null,
	currentUserDisplayName: string | null,
	currentUserId: string | null,
	callbacks: UseChatCallbacks,
): UseChatReturn {
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [isStreaming, setIsStreaming] = useState(false);
	const [contextWarning, setContextWarning] = useState<ContextWarningPayload | null>(null);

	// Ref to track streaming state inside the async callback
	const streamingRef = useRef(false);

	// Set to true just before hydrating messages from a session load so the
	// caller can scroll to the bottom instantly (behaviour: "instant") rather
	// than animating via smooth scroll as it would for a streaming append.
	const justLoadedRef = useRef(false);
	const [justLoaded, setJustLoaded] = useState(false);

	// Keep callbacks in a ref so sendMessage doesn't recreate when callbacks object changes.
	// App.tsx creates the callbacks inline on every render; this avoids stale closures.
	const callbacksRef = useRef(callbacks);
	callbacksRef.current = callbacks;

	// Ref to avoid clearing messages on the very first render (before any project is set)
	const isFirstProjectRef = useRef(true);
	const prevProjectIdRef = useRef<string | null>(selectedProjectId);

	// Tracks the most recently resolved session ID so handleApproveAction can persist
	// results even when the activeSessionId prop hasn't yet propagated from the parent.
	const resolvedSessionIdRef = useRef<string | null>(activeSessionId);

	// Keep resolvedSessionIdRef in sync with the activeSessionId prop
	// (the prop is the source of truth once the parent has caught up).
	useEffect(() => {
		resolvedSessionIdRef.current = activeSessionId;
	}, [activeSessionId]);

	// Clear messages on active project switch (not on initial render)
	useEffect(() => {
		const projectChanged = prevProjectIdRef.current !== selectedProjectId;
		prevProjectIdRef.current = selectedProjectId;

		if (isFirstProjectRef.current) {
			isFirstProjectRef.current = false;
			return;
		}

		if (projectChanged) {
			setMessages([]);
			setContextWarning(null);
		}
	}, [selectedProjectId]);

	// Load messages from DB when activeSessionId is set to a non-null value
	useEffect(() => {
		if (!activeSessionId) {
			// null means "new chat" — clear messages
			setMessages([]);
			return;
		}

		let cancelled = false;

		async function loadMessages(): Promise<void> {
			try {
				const response = await apiGet(`/api/chat/sessions/${activeSessionId}/messages`);

				if (cancelled) return;

				if (response.ok) {
					// The backend may include a top-level `chartData` field extracted
					// from metadata.chartSpec (see chat-sessions.ts mapMessage).
					const data = await response.json() as { messages: ChatMessageFromDb[] };
					const hydrated: ChatMessage[] = data.messages.map((dbMsg) => {
						const meta = dbMsg.metadata ?? {};

						return {
							id: dbMsg.id,
							role: toDisplayRole(dbMsg.role),
							content: dbMsg.content,
							timestamp: new Date(dbMsg.createdAt),
							userId: dbMsg.userId ?? undefined,
							senderDisplayName: dbMsg.senderDisplayName ?? undefined,
							model: dbMsg.modelUsed ?? undefined,
							// Hydrate classification fields for CCS (conversation context summary)
							complexity: dbMsg.complexity ?? undefined,
							intent: (
								meta.intent === "CHAT" ||
								meta.intent === "VISUALISATION" ||
								meta.intent === "ACTION"
							)
								? meta.intent as "CHAT" | "VISUALISATION" | "ACTION"
								: undefined,
							// Hydrate chart visualisation from persisted session if available
							chartData: dbMsg.chartData ?? undefined,
							// Hydrate action proposal fields so confirmation cards survive page refresh
							actionProposal: meta.actionProposal !== undefined
								? meta.actionProposal as ActionProposal
								: undefined,
							actionStatus: meta.actionStatus !== undefined
								? meta.actionStatus as ChatMessage["actionStatus"]
								: undefined,
							actionResult: meta.actionResult !== undefined
								? meta.actionResult as ActionResult
								: undefined,
						};
					});
					justLoadedRef.current = true;
					setJustLoaded(true);
					setMessages(hydrated);
				}
				// Silently ignore load failures — user can still chat
			} catch {
				// Network errors: leave messages as-is
			}
		}

		loadMessages();

		return () => {
			cancelled = true;
		};
	}, [activeSessionId]);

	const clearContextWarning = useCallback(() => {
		setContextWarning(null);
	}, []);

	const resetJustLoaded = useCallback(() => {
		justLoadedRef.current = false;
		setJustLoaded(false);
	}, []);

	/**
	 * Approve a proposed action.
	 *
	 * Sets the matching message to "executing", calls POST /api/actions/execute,
	 * then updates the message with the ActionResult (succeeded or failed).
	 */
	const handleApproveAction = useCallback(async (actionId: string) => {
		// Optimistically set the action status to executing
		setMessages((prev) =>
			prev.map((msg) =>
				msg.actionProposal?.actionId === actionId
					? { ...msg, actionStatus: "executing" as const }
					: msg,
			),
		);

		try {
			const response = await apiPost("/api/actions/execute", { actionId });

			if (response.ok) {
				const data = await response.json() as { success: boolean; message?: string; summary?: string; error?: string; results?: ActionResult["results"]; resultData?: ActionResult["resultData"] };
				// Use the actual success value from the backend
				const success = data.success ?? false;
				const result: ActionResult = {
					success,
					summary: data.message ?? data.summary ?? (success ? "Action completed successfully." : undefined),
					error: success ? undefined : (data.error ?? data.message ?? "Action failed."),
					results: data.results,
					resultData: data.resultData,
				};
				const actionStatus = success ? "succeeded" as const : "failed" as const;
				setMessages((prev) =>
					prev.map((msg) =>
						msg.actionProposal?.actionId === actionId
							? {
									...msg,
									actionStatus,
									actionResult: result,
								}
							: msg,
					),
				);
				// Persist the updated action result to the DB so it survives a page refresh.
				// Use the ref so we always have the most current session ID, even when the
				// activeSessionId prop hasn't yet propagated from the parent after creation.
				const sessionId = resolvedSessionIdRef.current;
				if (sessionId) {
					try {
						await apiPost(`/api/chat/sessions/${sessionId}/messages/action-result`, {
							actionId,
							actionResult: result,
							metadata: { actionResult: result, actionStatus },
						});
					} catch {
						// Persistence failure is non-critical — the in-memory state is correct
					}
				}
			} else {
				const errorData = await response.json().catch(() => ({})) as { error?: string; message?: string };
				const errorMessage = resolveActionErrorMessage(
					response.status,
					errorData.message ?? errorData.error,
				);
				const result: ActionResult = {
					success: false,
					error: errorMessage,
				};
				setMessages((prev) =>
					prev.map((msg) =>
						msg.actionProposal?.actionId === actionId
							? {
									...msg,
									actionStatus: "failed" as const,
									actionResult: result,
								}
							: msg,
					),
				);
			}
		} catch {
			// Network or parse failure — produce a friendly message without exposing raw error strings
			const result: ActionResult = {
				success: false,
				error: "An unexpected network error occurred. Please check your connection and try again.",
			};
			setMessages((prev) =>
				prev.map((msg) =>
					msg.actionProposal?.actionId === actionId
						? { ...msg, actionStatus: "failed" as const, actionResult: result }
						: msg,
				),
			);
		}
	}, []);

	/**
	 * Reject a proposed action.
	 *
	 * Calls POST /api/actions/:actionId/reject to notify the backend, then
	 * sets the matching message's actionStatus to "rejected" ONLY on success.
	 * On failure, sets actionError on the message so the user knows the
	 * cancellation did not go through and can retry.
	 */
	const handleRejectAction = useCallback(async (actionId: string) => {
		try {
			const response = await apiPost(`/api/actions/${actionId}/reject`, {});
			if (!response.ok) {
				throw new Error(`Reject failed: ${response.status}`);
			}
			// Only mark as rejected in the UI when the backend confirmed it
			setMessages((prev) =>
				prev.map((msg) =>
					msg.actionProposal?.actionId === actionId
						? { ...msg, actionStatus: "rejected" as const, actionError: undefined }
						: msg,
				),
			);
		} catch (err) {
			console.error("[reject] failed:", err);
			// Surface the error on the card so the user can retry
			setMessages((prev) =>
				prev.map((msg) =>
					msg.actionProposal?.actionId === actionId
						? { ...msg, actionError: "Failed to cancel. Please try again." }
						: msg,
				),
			);
		}
	}, []);

	/**
	 * Sends a message, streaming the response and persisting the exchange to the DB.
	 *
	 * Entity tokens embedded in the raw text (e.g. [@John Smith:user_12345]) are
	 * parsed via parseEntityTokens before the request is sent. The clean text
	 * (tokens replaced by names) is used as the display content and sent as the
	 * `message` field; the extracted entity references are sent separately as
	 * `entityReferences` for the AI to resolve.
	 *
	 * Uses a functional state updater pattern to read the current messages snapshot
	 * without adding `messages` to the dependency array (which would recreate this
	 * callback on every message, causing ChatInput to lose focus mid-conversation).
	 */
	const sendMessage = useCallback(async (text: string) => {
		if (streamingRef.current) return;

		// Parse entity tokens from the raw text before displaying or sending
		const { cleanMessage, entities } = parseEntityTokens(text);

		// Clear any previous context warning on new message send
		setContextWarning(null);

		// Read messages snapshot via functional updater (avoids adding messages to deps)
		let snapshotMessages: ChatMessage[] = [];
		setMessages((current) => {
			snapshotMessages = current;
			return current;
		});

		// Build conversation history from the snapshot (capped).
		// Include senderDisplayName for user messages so the backend can prefix
		// them with the sender's name for shared session AI context.
		const recentMessages = snapshotMessages.slice(-MAX_HISTORY_MESSAGES);
		const MAX_HISTORY_CONTENT = 7_500; // Keep under backend 8 000 char limit
		const conversationHistory = recentMessages.map((msg) => ({
			role: toModelRole(msg.role),
			content: msg.content.length > MAX_HISTORY_CONTENT
				? msg.content.slice(0, MAX_HISTORY_CONTENT) + "…"
				: msg.content,
			...(msg.role === "user" && msg.senderDisplayName
				? { senderDisplayName: msg.senderDisplayName }
				: {}),
		}));

		// Build lastAssistantContext from the most recent assistant message.
		// This gives the classifier awareness of the previous exchange (e.g.
		// whether it included a visualisation) for follow-up classification.
		const lastAssistant = [...snapshotMessages].reverse().find((m) => m.role === "assistant");
		const lastAssistantContext = lastAssistant
			? {
					intent: lastAssistant.intent ?? undefined,
					complexity: lastAssistant.complexity ?? undefined,
					chartType: lastAssistant.chartData?.type ?? undefined,
				}
			: undefined;

		// Add user message to display — show the clean text (tokens replaced by names).
		// userId is set so ChatMessage can identify this as the current user's own
		// message, even in team sessions where senderDisplayName is always populated.
		const userMessage: ChatMessage = {
			id: generateId(),
			role: "user",
			content: cleanMessage,
			timestamp: new Date(),
			userId: currentUserId ?? undefined,
			senderDisplayName: currentUserDisplayName ?? undefined,
		};

		// Create placeholder for assistant response
		const assistantPlaceholder: ChatMessage = {
			id: generateId(),
			role: "assistant",
			content: "",
			timestamp: new Date(),
		};

		setMessages((prev) => [...prev, userMessage, assistantPlaceholder]);
		setIsStreaming(true);
		streamingRef.current = true;

		// Capture classification metadata for saving to DB
		let capturedModel: string | null = null;
		let capturedComplexity: string | null = null;
		let capturedIntent: string | null = null;
		let capturedChartData: ChartSpec | null = null;
		let capturedActionProposal: ActionProposal | null = null;
		let capturedActionStatus: string | null = null;

		// Resolve or create session before sending
		let resolvedSessionId = activeSessionId;
		const isNewSession = !resolvedSessionId;

		try {
			if (!resolvedSessionId) {
				resolvedSessionId = await callbacksRef.current.onSessionNeeded(selectedProjectId);
				callbacksRef.current.onSessionIdChange(resolvedSessionId);
			}
			// Keep the ref current so handleApproveAction can access the session ID
			// before the activeSessionId prop propagates from the parent.
			resolvedSessionIdRef.current = resolvedSessionId;

			const response = await apiPost("/api/chat", {
				message: cleanMessage,
				projectId: selectedProjectId ?? undefined,
				conversationHistory,
				lastAssistantContext,
				// Send parsed entity references so the AI can resolve IDs from tokens.
				// Omitted (undefined) when no tokens were present in the message.
				entityReferences: entities.length > 0 ? entities : undefined,
			});

			if (!response.ok) {
				const errorBody = await response.json().catch(() => null) as { message?: string } | null;
				throw new Error(errorBody?.message || `Request failed with status ${response.status}`);
			}

			const reader = response.body?.getReader();
			if (!reader) {
				throw new Error("Response body is not readable");
			}

			// Track the accumulated assistant content for DB persistence
			let assistantContent = "";

			// Flag: set to true when we fire the early combined save inside the
			// action_proposed handler (satisfies C1 ordering requirement). When
			// true, the post-stream block skips the main save and only runs
			// auto-title / onMessagesSaved.
			let earlyNewSessionSaveFired = false;

			await parseSSEStream(reader, (sseEvent: SSEEvent) => {
				switch (sseEvent.event) {
					case "message": {
						try {
							const chunk: ChatStreamChunk = JSON.parse(sseEvent.data);

							if (chunk.done) {
								setIsStreaming(false);
								streamingRef.current = false;
								break;
							}

							if (chunk.text) {
								assistantContent += chunk.text;
								setMessages((prev) => {
									const updated = [...prev];
									const last = updated[updated.length - 1];
									if (last && last.role === "assistant") {
										updated[updated.length - 1] = {
											...last,
											content: last.content + chunk.text,
										};
									}
									return updated;
								});
							}
						} catch {
							// Skip malformed message chunks
						}
						break;
					}

					case "classification": {
						try {
							const payload: ClassificationPayload = JSON.parse(sseEvent.data);
							capturedModel = payload.model ?? null;
							capturedComplexity = payload.complexity ?? null;
							capturedIntent = payload.intent ?? null;
							// Merge classification metadata into the assistant message for display.
							setMessages((prev) => {
								const updated = [...prev];
								const last = updated[updated.length - 1];
								if (last && last.role === "assistant") {
									updated[updated.length - 1] = {
										...last,
										model: payload.model,
										complexity: payload.complexity,
										intent: payload.intent,
										hasProjectContext: payload.hasProjectContext,
										hasDocumentContext: payload.hasDocumentContext,
									};
								}
								return updated;
							});
						} catch {
							// Skip malformed classification events
						}
						break;
					}

					case "sources": {
						try {
							const payload: SourcesPayload = JSON.parse(sseEvent.data);
							setMessages((prev) => {
								const updated = [...prev];
								const last = updated[updated.length - 1];
								if (last && last.role === "assistant") {
									// Build source list: document sources from RAG.
									// Spread preserves workdriveFileId and documentId when the
									// backend includes them (Phase B onwards).
									const docSources = payload.sources.map((s) => ({
										...s,
										type: "document" as const,
									}));
									updated[updated.length - 1] = {
										...last,
										// Safety net: clear searching state when sources arrive
										isSearchingDocuments: false,
										sources: docSources,
									};
								}
								return updated;
							});
						} catch {
							// Skip malformed sources
						}
						break;
					}

					case "chart_data": {
						try {
							const payload = JSON.parse(sseEvent.data) as ChartDataPayload;
							capturedChartData = payload.chartSpec;
							setMessages((prev) => {
								const updated = [...prev];
								const last = updated[updated.length - 1];
								if (last && last.role === "assistant") {
									updated[updated.length - 1] = { ...last, chartData: payload.chartSpec };
								}
								return updated;
							});
						} catch {
							// Skip malformed chart_data events
						}
						break;
					}

					case "retrieval_start": {
						setMessages((prev) => {
							const updated = [...prev];
							const last = updated[updated.length - 1];
							if (last && last.role === "assistant") {
								updated[updated.length - 1] = { ...last, isSearchingDocuments: true };
							}
							return updated;
						});
						break;
					}

					case "retrieval_end": {
						try {
							const payload: RetrievalEndPayload = JSON.parse(sseEvent.data);
							setMessages((prev) => {
								const updated = [...prev];
								const last = updated[updated.length - 1];
								if (last && last.role === "assistant") {
									updated[updated.length - 1] = {
										...last,
										isSearchingDocuments: false,
										retrievalStatus: payload.status,
									};
								}
								return updated;
							});
						} catch {
							// Skip malformed retrieval_end event
						}
						break;
					}

					case "context_warning": {
						try {
							const payload: ContextWarningPayload = JSON.parse(sseEvent.data);
							setContextWarning(payload);
						} catch {
							// Skip malformed warning
						}
						break;
					}

					case "action_progress": {
						try {
							const payload = JSON.parse(sseEvent.data) as { stage: string; label: string };
							setMessages((prev) => {
								const updated = [...prev];
								const last = updated[updated.length - 1];
								if (last && last.role === "assistant") {
									updated[updated.length - 1] = {
										...last,
										actionProgressLabel: payload.label,
									};
								}
								return updated;
							});
						} catch {
							// Skip malformed progress events
						}
						break;
					}

					case "action_proposed": {
						// The worker has identified a write-back action via Gemini function calling.
						// Store the proposal on the current assistant message so the confirmation
						// card can be rendered. Malformed JSON is silently discarded.
						try {
							const proposal: ActionProposal = JSON.parse(sseEvent.data);
							// Capture for DB persistence alongside the assistant message
							capturedActionProposal = proposal;
							capturedActionStatus = "pending";
							setMessages((prev) => {
								const updated = [...prev];
								const last = updated[updated.length - 1];
								if (last && last.role === "assistant") {
									updated[updated.length - 1] = {
										...last,
										actionProposal: proposal,
										actionStatus: "pending",
										actionProgressLabel: undefined,
									};
								}
								return updated;
							});

							// Fire a user-only save immediately when an action is proposed.
							// This ensures the user message is always persisted on action-only
							// turns (where assistantContent stays empty — the backend rejects
							// empty content, so we cannot include the assistant message here).
							// The assistant message is saved post-stream using proposal.description
							// as a content fallback when no prose was streamed.
							// Fire-and-forget IIFE so it does not block the stream continuing.
							if (resolvedSessionId && !earlyNewSessionSaveFired) {
								earlyNewSessionSaveFired = true;
								const sessionIdForSave = resolvedSessionId;
								void (async () => {
									try {
										const saveResp = await apiPost(
											`/api/chat/sessions/${sessionIdForSave}/messages`,
											{
												messages: [
													{
														role: toModelRole("user"),
														content: cleanMessage,
														sortOrder: snapshotMessages.length + 1,
													},
												],
											},
										);
										if (!saveResp.ok) {
											console.warn(
												`[chat] user message save failed (sessionId=${sessionIdForSave}): HTTP ${saveResp.status}`,
											);
										}
									} catch (err) {
										console.warn(
											`[chat] user message save failed (sessionId=${sessionIdForSave}):`,
											err instanceof Error ? err.message : err,
										);
									}
								})();
							}
						} catch {
							// Discard malformed action_proposed events — do not crash the stream
						}
						break;
					}

					case "error": {
						try {
							const payload: ChatErrorPayload = JSON.parse(sseEvent.data);
							setMessages((prev) => {
								const updated = [...prev];
								const last = updated[updated.length - 1];
								if (last && last.role === "assistant") {
									updated[updated.length - 1] = {
										...last,
										content: payload.message,
										isError: true,
									};
								}
								return updated;
							});
							setIsStreaming(false);
							streamingRef.current = false;
						} catch {
							// Skip malformed error
						}
						break;
					}
				}
			});

			// Post-stream save.
			//
			// Two paths depending on whether action_proposed fired mid-stream:
			//
			// 1. earlyNewSessionSaveFired (action turn) — the user message was saved
			//    as a fire-and-forget inside action_proposed. Now save only the
			//    assistant message, using proposal.description as a content fallback
			//    when no prose was streamed (the backend rejects empty content).
			//
			// 2. Standard path — save both user and assistant messages together.
			//    Fires when assistantContent is non-empty (normal chat turn) or when
			//    this is a new session (user message must always be persisted).
			if (resolvedSessionId && earlyNewSessionSaveFired) {
				// Action turn — save the assistant message with proposal.description
				// as a content fallback so the backend validator accepts it.
				// Cast to ActionProposal | null — TypeScript loses track of this
				// mutable closure variable across async boundaries.
				const actionProposalSnapshot = capturedActionProposal as ActionProposal | null;
				const assistantContentForSave = assistantContent
					|| actionProposalSnapshot?.description
					|| "";

				if (assistantContentForSave) {
					try {
						const saveResp = await apiPost(
							`/api/chat/sessions/${resolvedSessionId}/messages`,
							{
								messages: [
									{
										role: toModelRole("assistant"),
										content: assistantContentForSave,
										modelUsed: capturedModel ?? undefined,
										complexity: capturedComplexity ?? undefined,
										chartData: capturedChartData ?? undefined,
										sortOrder: snapshotMessages.length + 2,
										metadata: {
											...(capturedIntent ? { intent: capturedIntent } : {}),
											...(capturedActionProposal ? { actionProposal: capturedActionProposal } : {}),
											...(capturedActionStatus ? { actionStatus: capturedActionStatus } : {}),
										},
									},
								],
							},
						);
						if (!saveResp.ok) {
							console.warn(
								`[chat] assistant message save failed (sessionId=${resolvedSessionId}): HTTP ${saveResp.status}`,
							);
						}
					} catch (err) {
						console.warn(
							`[chat] assistant message save failed (sessionId=${resolvedSessionId}):`,
							err instanceof Error ? err.message : err,
						);
					}
				}

				// Auto-title the session on its first message
				if (isNewSession) {
					const autoTitle = cleanMessage.slice(0, 80);
					await apiPatch(`/api/chat/sessions/${resolvedSessionId}`, { title: autoTitle });
				}
				callbacksRef.current.onMessagesSaved();
			} else if (resolvedSessionId && (assistantContent || isNewSession)) {
				try {
					// Always include the user message. Only include the assistant message
					// when content is non-empty — the backend validator rejects empty content.
					const messagesToSave: Array<Record<string, unknown>> = [
						{
							role: toModelRole("user"),
							content: cleanMessage,
							sortOrder: snapshotMessages.length + 1,
						},
					];

					if (assistantContent) {
						messagesToSave.push({
							role: toModelRole("assistant"),
							content: assistantContent,
							modelUsed: capturedModel ?? undefined,
							complexity: capturedComplexity ?? undefined,
							chartData: capturedChartData ?? undefined,
							sortOrder: snapshotMessages.length + 2,
							// Store intent and action fields in metadata so they can be hydrated
							// on session reload (lastAssistantContext, confirmation card, etc.).
							metadata: (capturedIntent || capturedActionProposal)
								? {
									...(capturedIntent ? { intent: capturedIntent } : {}),
									...(capturedActionProposal ? { actionProposal: capturedActionProposal } : {}),
									...(capturedActionStatus ? { actionStatus: capturedActionStatus } : {}),
								}
								: undefined,
						});
					}

					const saveResponse = await apiPost(`/api/chat/sessions/${resolvedSessionId}/messages`, {
						messages: messagesToSave,
					});

					if (!saveResponse.ok) {
						console.warn(
							`[chat] message save failed (sessionId=${resolvedSessionId}): HTTP ${saveResponse.status}`,
						);
					} else {
						// Auto-title the session on its first message pair
						if (isNewSession) {
							const autoTitle = cleanMessage.slice(0, 80);
							await apiPatch(`/api/chat/sessions/${resolvedSessionId}`, { title: autoTitle });
						}

						callbacksRef.current.onMessagesSaved();
					}
				} catch (err) {
					console.warn(
						`[chat] message save failed (sessionId=${resolvedSessionId}):`,
						err instanceof Error ? err.message : err,
					);
				}
			}
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
			setMessages((prev) => {
				const updated = [...prev];
				const last = updated[updated.length - 1];
				if (last && last.role === "assistant") {
					updated[updated.length - 1] = {
						...last,
						content: errorMessage,
						isError: true,
					};
				}
				return updated;
			});
		} finally {
			setIsStreaming(false);
			streamingRef.current = false;
		}
	}, [selectedProjectId, activeSessionId]);

	return {
		messages,
		isStreaming,
		contextWarning,
		sendMessage,
		clearContextWarning,
		handleApproveAction,
		handleRejectAction,
		justLoaded,
		resetJustLoaded,
	};
}

/**
 * Parses inline entity tokens from a raw message string.
 *
 * Token format:
 *   User       → [@Name:user_ID]
 *   Task       → [#Name:task_ID]
 *   Milestone  → [!Name:milestone_ID]
 *   Status     → [$Name:status_ID]
 *
 * Returns:
 *   - cleanMessage: the original string with tokens replaced by just the entity name
 *   - entities:     array of { type, id, name } for each resolved token
 *
 * Malformed tokens (missing brackets, unrecognised type suffix) are left unchanged
 * in cleanMessage and do not produce entity entries.
 */
export function parseEntityTokens(message: string): {
	cleanMessage: string;
	entities: Array<{ type: string; id: string; name: string }>;
} {
	if (!message) {
		return { cleanMessage: "", entities: [] };
	}

	const TOKEN_REGEX = /\[[@#!$][^\]]+?:(user|task|milestone|status)_([^\]]*)\]/g;
	const entities: Array<{ type: string; id: string; name: string }> = [];

	const cleanMessage = message.replace(TOKEN_REGEX, (match) => {
		// Remove the leading two chars ([@ / [# / [! / [$) and the trailing ]
		const innerContent = match.slice(2, -1);

		// Split on the LAST colon — the boundary between name and type_id
		const lastColonIdx = innerContent.lastIndexOf(":");
		if (lastColonIdx === -1) {
			return match; // Malformed — no colon found; leave unchanged
		}

		const name = innerContent.substring(0, lastColonIdx).trim();
		const typeAndId = innerContent.substring(lastColonIdx + 1);

		const underscoreIdx = typeAndId.indexOf("_");
		if (underscoreIdx === -1) {
			return match; // Malformed — no underscore separator; leave unchanged
		}

		const type = typeAndId.substring(0, underscoreIdx);
		const id = typeAndId.substring(underscoreIdx + 1);

		entities.push({ type, id, name });
		return name;
	});

	return { cleanMessage, entities };
}
